import React, { useState } from "react";
import styles from "./styles.module.scss";
import classNames from "classnames";
import { ReactComponent as AISummaryPlayerIcon } from "assets/icons/ic_player_ai_summary.svg";
import SmallLoadingIndicator from "../small-loading-indicator/smal-loading-indicator";
import DotsLoadingIndicator from "../dots-loading-indicator/dots-loading-indicator";
import { useAppStore } from "store";
import { SummaryStatus } from "models/dto/ZoomiLxp/Utilities/Enumerations/SummaryStatus";
import { IBaseSummaryModel } from "models/dto/ZoomiLxp/Models/Courses/IBaseSummaryModel";
import { getCourseItemSummaryInfo } from "helpers/content.helper";
import { ContentItemTypes } from "models/dto/ZoomiLxp/Utilities/Enumerations/ContentItemTypes";

export enum EAISummaryType {
	CONTENT = "content",
	COURSE = "course",
}

interface IAISummaryProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	summaryType: EAISummaryType;
	status?: SummaryStatus;
	ID?: number;
	isAISummaryShowed?: boolean;
	btnType?: "text" | "icon";
	textToGenerate?: string;
	textToUndo?: string;
	actionOnSuccess?: (response?: IBaseSummaryModel) => void;
	actionToUndo?: () => void;
	onClickHandler?: () => void;
	className?: string;
	isDraft?: boolean;
	isForcedGeneration?: boolean;
}

const cx = classNames.bind(styles);

const BUTTON_TYPE = "text";
const DEFAULT_TEXT = "View AI Summary...";
const DEFAULT_UNDO_TEXT = "View Description";
export const DEFAULT_AI_SUMMARY_FAILED_TEXT = `Sorry, we couldn't generate summary… It may happen because of following reasons:
	- no response from AI, please retry later
	- no text is available for summary, please make sure that content contain text
`;

const AISummaryButton = (props: IAISummaryProps) => {
	const {
		isAISummaryShowed = false,
		btnType = BUTTON_TYPE,
		textToGenerate = DEFAULT_TEXT,
		textToUndo = DEFAULT_UNDO_TEXT,
		status,
		actionToUndo,
		actionOnSuccess,
		className,
		summaryType,
		ID,
		isDraft = false,
		isForcedGeneration = false,
		onClickHandler,
		...otherProps
	} = props;
	const [loading, setLoading] = useState(false);

	const { coursesStore, contentStore, aiSummaryStore } = useAppStore();

	summaryType === EAISummaryType.COURSE && status === SummaryStatus.PENDING && !loading && setLoading(true);
	summaryType === EAISummaryType.CONTENT && coursesStore.curActiveContent.content?.summary?.status === SummaryStatus.PENDING && !loading && setLoading(true);
	
	if(loading && btnType === "icon") return <SmallLoadingIndicator/>;
	if(loading && btnType === "text") return <DotsLoadingIndicator/>;

	const summaryCourseFn = isDraft
		? coursesStore.getDraftCourseAISummary.bind(coursesStore)
		: coursesStore.getCourseAISummary.bind(coursesStore);

	const summaryContentFn = isDraft
		? contentStore.getDraftContentAISummary.bind(contentStore)
		: contentStore.getContentAISummary.bind(contentStore);

	const getContentItemId = (type : ContentItemTypes) => {
		if (type === ContentItemTypes.ContentSegment) {
			return coursesStore.curActiveContent.contentSegmentId;
		}
		return coursesStore.curActiveContent.contentId;
	};

	const onClick = async () => {
		if (onClickHandler) {
			onClickHandler();
		}
		if (!isForcedGeneration && isAISummaryShowed) {
			actionToUndo && actionToUndo();
			return;
		}

		setLoading(true);
		if (summaryType === EAISummaryType.COURSE) {
			if (!status || status === SummaryStatus.FAILED || isForcedGeneration) {
				summaryCourseFn()
					.then((res) => actionOnSuccess && actionOnSuccess(res))
					.catch((err) => console.log(err))
					.finally(() => setLoading(false));
			} else {
				setLoading(false);
				actionOnSuccess && actionOnSuccess();
			}
		}

		const contentItemId = getContentItemId(coursesStore.curActiveContent.contentItemType);
		if(summaryType === EAISummaryType.CONTENT && (contentItemId || ID)) {
			const contentId = ID || contentItemId;
			if(aiSummaryStore.isShowText && aiSummaryStore.initialType === "CONTENT") {
				setLoading(false);
				aiSummaryStore.isShowSegmentAlert = false;
				aiSummaryStore.isShowText = false;
				aiSummaryStore.initialType = null;
				return;
			}
			if(contentId) {
				if(actionOnSuccess) {
					summaryContentFn(contentId)
						.then((res) => actionOnSuccess(res))
						.catch((err) => console.log(err))
						.finally(() => setLoading(false));
				} else {
					// This is edge case for content button in player
					// Avoiding to include any content based logic in component for player control
					aiSummaryStore.initialType = "CONTENT";
					if(!aiSummaryStore.contentSummary) {
						contentStore
							.getContentAISummary(contentId, coursesStore.curActiveContent.contentItemType)
							.then((res) => {
								if (res.status === SummaryStatus.FAILED) {
									aiSummaryStore.contentSummary = DEFAULT_AI_SUMMARY_FAILED_TEXT;
								} else {
									aiSummaryStore.contentSummary = res.gptSummary;
								}
								aiSummaryStore.isShowText = true;
							})
							.catch((err) => console.log(err))
							.finally(() => setLoading(false));
					} else {
						aiSummaryStore.isShowText = true;
						setLoading(false);
					}
				}
			}
		}
	};

	if((summaryType === EAISummaryType.CONTENT && !ID) && !getCourseItemSummaryInfo(coursesStore.curActiveContent).canGenerateSummary) {
		return<></>;
	}

	return (
		<button
			onClick={onClick}
			className={cx(styles.ai_summary_button, className, {[styles.ai_summary_button__link]: btnType === "text", [styles.ai_summary_button__icon]: btnType === "icon", [styles.ai_summary_button__disabled]: otherProps.disabled})}
			{...otherProps}
		>
			{btnType === "text" ? isAISummaryShowed ? textToUndo : textToGenerate : <></>}
			{btnType === "icon" ? <AISummaryPlayerIcon className={styles.ai_summary_button__icon}/> : <></>}
		</button>
	);
};

export default AISummaryButton;
